import { initialUserReview, THERAPIES_ACTIONS } from './therapies.actions';
import * as immutable from 'immutable';

import { IgenomicsAction } from '@ispm/core/interfaces/genomics-action.interface';
import { WorkflowDrivenCacheablesEntity } from '@ispm/workflow/entities/workflow-driven-cacheables.entity';
import { CORE_ACTIONS } from '@ispm/core/constants/core-actions.constant';
import { Idrug } from '../interfaces/drug.interface';
import { TherapiesActionCreatorService } from '../services/therapies-action-creator.service';
import { AberrationHandler } from '@ispm/aberrations/class/aberration-handler.class';
import { ITreatmentGlobalConf } from '@ispm/therapies/interfaces/view-configuration.interface';

export interface ItherapyState {
  therapies: immutable.Map<string, any>;
  providers: immutable.Map<string, Array<string>>;
  uncharacterizedTherapies: immutable.Map<string, any>;
  notSentForInterpretationTherapies: immutable.Map<string, any>;
  opinion: immutable.Map<string, any>;
  trials: immutable.Map<string, any>;
  treatments: immutable.Map<string, any>;
  clinicalrecommendations: immutable.Map<string, any>;
  reviews: immutable.Map<string, any>;
  globals: immutable.Map<string, ITreatmentGlobalConf>;
  anyResources: immutable.Map<string, any>;
  permittedActions: immutable.Map<string, any>;
  userReview: {
    [cacheKey: string]: {
      caseStatus: boolean,
      rationale: string,
      priority: immutable.List<Idrug>
      therapiesIndex: immutable.Set<string>
    }
  };
}

const InitTherapyState: ItherapyState = {
  therapies: <immutable.Map<string, any>> immutable.Map(),
  providers: <immutable.Map<string, Array<string>>> immutable.Map(),
  uncharacterizedTherapies: <immutable.Map<string, any>> immutable.Map(),
  notSentForInterpretationTherapies: <immutable.Map<string, any>> immutable.Map(),
  opinion: <immutable.Map<string, any>> immutable.Map(),
  trials: <immutable.Map<string, any>> immutable.Map(),
  treatments: <immutable.Map<string, any>> immutable.Map(),
  clinicalrecommendations: <immutable.Map<string, any>> immutable.Map(),
  reviews: <immutable.Map<string, any>> immutable.Map(),
  globals: <immutable.Map<string, ITreatmentGlobalConf>> immutable.Map(),
  anyResources: <immutable.Map<string, any>> immutable.Map(),
  permittedActions: <immutable.Map<string, any>> immutable.Map(),
  userReview: { }
};

export function therapiesReducer(state: ItherapyState = InitTherapyState, action: IgenomicsAction<WorkflowDrivenCacheablesEntity>): ItherapyState {
  switch (action.type) {
    // Set opinion for include in report
    case THERAPIES_ACTIONS.SET_OPINION:
      state.opinion = state.opinion.set(action.payload.getIdentifier(), action.payload.getValue());
      return { ...state};

    // Treatments & trials set
    case THERAPIES_ACTIONS.SET_THERAPIES:
      setTherapyAberrationProxyHandler(action.payload.getValue());
      state.therapies = state.therapies.set(action.payload.getIdentifier(), action.payload.getValue() );
      return { ...state};
    case THERAPIES_ACTIONS.SET_UNCHARACTERIZEDTHERAPIES:
      setTherapyAberrationProxyHandler(action.payload.getValue());
      state.uncharacterizedTherapies = state.uncharacterizedTherapies.set(action.payload.getIdentifier(), action.payload.getValue() );
      return { ...state};
    case THERAPIES_ACTIONS.SET_NOT_SENT_FOR_INTERPRETATION_THERAPIES:
      setTherapyAberrationProxyHandler(action.payload.getValue());
      state.notSentForInterpretationTherapies = state.notSentForInterpretationTherapies.set(action.payload.getIdentifier(), action.payload.getValue() );
      return { ...state};
    case THERAPIES_ACTIONS.SET_PROVIDERS:
      state.providers = state.providers.set(action.payload.getIdentifier(), action.payload.getValue() );
      return { ...state};
    case THERAPIES_ACTIONS.SET_TRIALS:
      state.trials = state.trials.set(action.payload.getIdentifier(), action.payload.getValue() );
      return { ...state};
    case THERAPIES_ACTIONS.SET_TREATMENTS:
      state.treatments =  state.treatments.set(action.payload.getIdentifier(), updateTreatments(state, action));
      return { ...state};

    // ClinicalRecommendations
    case THERAPIES_ACTIONS.CLEAR_CLINICALRECOMMENDATIONS:
      state.clinicalrecommendations = state.clinicalrecommendations.set(action.payload.getIdentifier(), { });
      return state;
    case THERAPIES_ACTIONS.SET_CLINICALRECOMMENDATIONS:
      state.clinicalrecommendations = state.clinicalrecommendations.set(action.payload.getIdentifier(), action.payload.getValue());
      return state;
    case THERAPIES_ACTIONS.APPEND_CLINICALRECOMMENDATIONS:
      const identifier = action.payload.getIdentifier();
      let recommendations = state.clinicalrecommendations.get(identifier);

      if (!Array.isArray(recommendations)) {
        recommendations = [];
      }

      const values = action.payload.getValue();
      recommendations = recommendations.filter((recommendation) => {
        return !values.some(newValue => recommendation.id === newValue.id);
      }, values).concat(values);
      state.clinicalrecommendations = state.clinicalrecommendations.set(identifier, recommendations);

      return state;
    case THERAPIES_ACTIONS.UPDATE_CLINICALRECOMMENDATIONS:
      const mainId = action.payload.getIdentifier();
      let clinicalRecommendations = state.clinicalrecommendations.get(mainId);
      const newValue = action.payload.getValue();

      clinicalRecommendations = clinicalRecommendations.map((recommendation) => {
        return recommendation.id === newValue.id ? action.payload.getValue() : recommendation;
      });
      state.clinicalrecommendations = state.clinicalrecommendations.set(mainId, clinicalRecommendations);

      return { ...state};

    // Other resources
    case THERAPIES_ACTIONS.SET_ANY_RESOURCES:
      state.anyResources =  state.anyResources.set(action.payload.getIdentifier(), action.payload.getValue());
      return state;
    case THERAPIES_ACTIONS.SET_PERMITTED_ACTIONS:
      state.permittedActions =  state.anyResources.set(action.payload.getIdentifier(), action.payload.getValue());
      return state;
    // Priority handling
    case THERAPIES_ACTIONS.SET_CASE_STATUS:
      state.userReview = updateUserReview(state, action, { caseStatus: action.payload.getValue()});
      return { ...state};
    case THERAPIES_ACTIONS.UPDATE_RATIONALE:
        state.userReview = updateUserReview(state, action, { rationale: action.payload.getValue()});
      return { ...state};
    case THERAPIES_ACTIONS.CHANGE_ORDER:
      removeTherapyFromPriority(state, action);
      addTherapyToPriority(state, action);
      return { ...state};
    case THERAPIES_ACTIONS.REMOVE_THERAPY_FROM_PRIORITY_LIST:
      return removeTherapyFromPriority(state, action);
    case THERAPIES_ACTIONS.ADD_THERAPY_TO_PRIORITY_LIST:
      return addTherapyToPriority(state, action);
    case THERAPIES_ACTIONS.SET_PRIORITY_LIST:
      return addFromList(state, action);
    case THERAPIES_ACTIONS.UPDATE_ITEM_PRIORITY_LIST: {
      const payload = action.payload.getValue();
      const activeRecord = action.payload.getCachedRecordFromState(state.userReview);
      activeRecord.priority = activeRecord.priority.set(payload.index, payload.value);
      state.userReview = action.payload.upsert(state.userReview, null, activeRecord);
      return { ...state};
    }

    // Reviews handling
    case THERAPIES_ACTIONS.UPDATE_REVIEW:
      const reviewsToSet = state.reviews.get(action.payload.getIdentifier());
      const { index, review} = action.payload.getValue();
      reviewsToSet[index] = review;
      state.reviews = state.reviews.set(action.payload.getIdentifier(), reviewsToSet);
      return { ...state};
    case THERAPIES_ACTIONS.SET_REVIEWS:
      state.reviews = state.reviews.set(action.payload.getIdentifier(), action.payload.getValue());
      return { ...state};
    case THERAPIES_ACTIONS.ADD_REVIEWS:
      const reviews = action.payload.getCachedRecordFromState(state.reviews);
      state.reviews = state.reviews.set(action.payload.getIdentifier(), [...reviews, ...action.payload['reviews']]);
      return { ...state};
    case THERAPIES_ACTIONS.INIT_REVIEW:
      state.userReview = action.payload.init(state.userReview, action.payload.getIdentifier(), initialUserReview);
      return { ...state};

    // Configuration set
    case THERAPIES_ACTIONS.SET_GLOBAL_CONF:
      state.globals = action.payload.upsert(state.globals);
      return { ...state};

    // Cache handling
    case CORE_ACTIONS.CLEAR_ALL_CACHED_ITEMS:
    case THERAPIES_ACTIONS.INIT_THERAPY_STATE:
      return { ...InitTherapyState};
    case THERAPIES_ACTIONS.UPDATE_TREATMENT_WORKFLOW_DRIVEN:
      return { ...state};
    default:
      return state;
  }
}

function removeTherapyFromPriority(state, action) {
  const activeUserReview = action.payload.getCachedRecordFromState(state.userReview);
  const priorityArray = activeUserReview.priority.toArray();
  const actionValue = action.payload.getValue();
  let key, index;

  for (key in priorityArray) {
    if (TherapiesActionCreatorService.getTherapyIdentifier(priorityArray[key], actionValue.indexProperty) === actionValue.id) {
      index = key;
      break;
    }
  }

  if (index) {
    activeUserReview.priority = activeUserReview.priority.deleteIn([index]);
  }

  activeUserReview.therapiesIndex = activeUserReview.therapiesIndex.delete(actionValue.id);
  state.userReview = action.payload.upsert(state.userReview, null, activeUserReview);

  return { ...state};
}

function addFromList(state, action) {
  const actionValue = action.payload.getValue();
  let therapies = actionValue.list;
  const indexProperty = actionValue.indexProperty;
  state.userReview = action.payload.upsert(state.userReview, 'priority', immutable.List(therapies));
  const therapiesIndexes = immutable.Set().asMutable();
  therapies = Array.from(therapies);

  for (const therapy of therapies) {
    therapiesIndexes.add(TherapiesActionCreatorService.getTherapyIdentifier(therapy, indexProperty));
  }
  state.userReview = action.payload.upsert(state.userReview, 'therapiesIndex', therapiesIndexes.asImmutable());

  return { ...state};
}

function addTherapyToPriority(state, action) {
  const activeUserReview = action.payload.getCachedRecordFromState(state.userReview);
  const index = action.payload.getValue().index === null
    ? activeUserReview.priority.size + 1
    : action.payload.getValue().index;

  activeUserReview.priority = activeUserReview.priority.insert(index, action.payload.getValue().therapy).asImmutable();
  activeUserReview.therapiesIndex = activeUserReview.therapiesIndex.add(action.payload.getValue().identifier).asImmutable();
  state.userReview = action.payload.upsert(state.userReview, null, activeUserReview);

  return { ...state};
}

function updateUserReview(state, action, updateValue) {
  let activeRecord = action.payload.getCachedRecordFromState(state.userReview);
  activeRecord = Object.assign({ }, initialUserReview, activeRecord, updateValue);

  return action.payload.upsert(state.userReview, null , activeRecord);
}

function setTherapyAberrationProxyHandler(therapies) {
  for (const therapy of therapies) {
    if (therapy.hasOwnProperty('aberration')) {
      AberrationHandler.defineGeneHandler(therapy.aberration);
    }
  }

  return therapies;
}

function updateTreatments(state, action) {
  const treatmentToUpdateMap = new Map();
  action.payload.getValue().forEach(item => treatmentToUpdateMap.set(item.id, item));

  if (state && state.treatments.size > 0 && state.treatments.has(action.payload.getIdentifier())) {
    return state.treatments.get(action.payload.getIdentifier()).map(item => {
      if (treatmentToUpdateMap.get(item.id)) {
        return treatmentToUpdateMap.get(item.id);
      }

      return item;
    });
  }

  return action.payload.getValue();
}
